import { Box, Button, Card, CardActions, CardContent, Chip, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CreateFormQuestionOptionBody,
    MoveFormQuestionOptionBody,
    UpdateFormQuestionOptionBody,
} from '../../services/apiSLTP/qhse/forms/formQuestionOptions';
import { UpdateFormQuestionBody } from '../../services/apiSLTP/qhse/forms/formQuestions';
import { UpdateFormSectionBody } from '../../services/apiSLTP/qhse/forms/formSections';
import { FormTemplateWithSections, UpdateFormTemplateBody } from '../../services/apiSLTP/qhse/forms/formTemplates';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import ScrollPageContainer from '../layout/ScrollPageContainer';
import FileUpload from '../ui/FileUpload';
import Icon from '../ui/Icon';
import SectionItem from './sections/SectionItem';

interface FormEditorProps {
    formTemplate?: FormTemplateWithSections;
    onAddQuestion: (sectionId: string, type: number, order: number, setSelectedId: (id: string | null) => void) => void;
    onAddSection: (templateId: string, order: number, setSelectedId: (id: string | null) => void) => void;
    onMoveSection: (sectionId: string, order: number) => void;
    onMoveQuestion: (questionId: string, toSectionId: string, order: number) => void;
    onUpdateQuestion: (questionId: string, data: UpdateFormQuestionBody) => void;
    onUpdateSection: (sectionId: string, data: UpdateFormSectionBody) => void;
    onUpdateTemplate: (templateId: string, data: UpdateFormTemplateBody, file?: File) => void;
    onDuplicateQuestion: (questionId: string, setSelectedId: (id: string | null) => void) => void;
    onDeleteQuestion: (questionId: string) => void;
    onDuplicateSection: (sectionId: string, setSelectedId: (id: string | null) => void) => void;
    onDeleteSection: (sectionId: string, withContent: boolean) => void;
    onAddQuestionOption: (data: CreateFormQuestionOptionBody) => void;
    onDeleteQuestionOption: (optionId: string) => void;
    onUpdateQuestionOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onMoveQuestionOption: (optionId: string, data: MoveFormQuestionOptionBody) => void;
    disabled?: boolean;
}

const FormEditor: React.FC<FormEditorProps> = ({
    formTemplate,
    onAddQuestion,
    onAddSection,
    onMoveSection,
    onMoveQuestion,
    onUpdateQuestion,
    onUpdateSection,
    onUpdateTemplate,
    onDuplicateQuestion,
    onDeleteQuestion,
    onDuplicateSection,
    onDeleteSection,
    onAddQuestionOption,
    onDeleteQuestionOption,
    onUpdateQuestionOption,
    onMoveQuestionOption,
    disabled,
}) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const [sections, setSections] = useState(formTemplate?.sections || []);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formColor, setFormColor] = useState<string>(formTemplate?.type?.color || '#1976d2');
    const [isDragging, setIsDragging] = useState(false);
    const [draggingItemId, setDraggingItemId] = useState<string | null>(null);

    const [formTitle, setFormTitle] = useState(formTemplate?.title || '');
    const [formDescription, setFormDescription] = useState(formTemplate?.description || '');
    const [questionsNumbering, setQuestionsNumbering] = useState<{ [key: string]: number }>({});

    const moveQuestion = useCallback((fromSectionIndex: number, fromIndex: number, toSectionIndex: number, toIndex: number) => {
        setSections((prev) => {
            const newSections = [...prev];
            const fromSection = { ...newSections[fromSectionIndex] };
            const toSection = fromSectionIndex === toSectionIndex ? fromSection : { ...newSections[toSectionIndex] };

            fromSection.questions = [...fromSection.questions];
            if (fromSectionIndex !== toSectionIndex) {
                toSection.questions = [...toSection.questions];
            }

            const [movedQuestion] = fromSection.questions.splice(fromIndex, 1);
            toSection.questions.splice(toIndex, 0, movedQuestion);

            newSections[fromSectionIndex] = fromSection;
            if (fromSectionIndex !== toSectionIndex) {
                newSections[toSectionIndex] = toSection;
            }

            return newSections;
        });
    }, []);

    useEffect(() => setSections(formTemplate?.sections || []), [formTemplate?.sections]);

    useEffect(() => {
        if (isDragging) return;

        const numbering: { [key: string]: number } = {};
        let counter = 1;
        sections.forEach((section) => section.questions.forEach((q) => (numbering[q._id] = counter++)));
        setQuestionsNumbering(numbering);
    }, [isDragging, formTemplate?.sections, sections]);

    useEffect(() => {
        if (!isDragging) return;
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const margin = 200;
            const scrollSpeed = 10;

            const y = e.clientY - containerRect.top + container.scrollTop;

            if (y < container.scrollTop + margin) {
                container.scrollTop -= scrollSpeed;
            } else if (y > container.scrollTop + container.clientHeight - margin) {
                container.scrollTop += scrollSpeed;
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [isDragging]);

    const handleGlobalDrag = useCallback((dragging: boolean, itemId: string | null) => {
        setIsDragging(dragging);
        setDraggingItemId(itemId);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectedId(null);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <ScrollPageContainer ref={scrollContainerRef} disabled={disabled}>
            <Card sx={{ borderRadius: 0 }}>
                <CardContent
                    sx={{
                        borderLeft: selectedId === formTemplate?._id ? `6px solid ${formColor}` : '6px solid transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {selectedId !== formTemplate?._id ? (
                        <Card
                            sx={{
                                borderLeft: '6px solid transparent',
                                cursor: 'pointer',
                                bgcolor: 'transparent',
                                p: 0,
                                borderRadius: 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: selectedId === formTemplate?._id ? 'action.selected' : 'action.hover',
                                },
                            }}
                            onClick={() => setSelectedId(formTemplate?._id || null)}
                            elevation={0}
                        >
                            <CardContent sx={{ p: 2, gap: 1, display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                                    {formTitle}
                                </Typography>
                                {formDescription && (
                                    <Typography variant="subtitle1" component="div" sx={{ whiteSpace: 'pre-line' }}>
                                        {formDescription}
                                    </Typography>
                                )}
                                {formTemplate?.supportDocumentFile && (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Icon name="insertDriveFile" />
                                        <Typography
                                            component="a"
                                            href={formTemplate.supportDocumentFile.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                textDecoration: 'none',
                                                color: 'primary.main',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '100%',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {formTemplate.supportDocumentFile.fileName}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label={capitalizeFirstLetter(t('title'))}
                                value={formTitle}
                                onChange={(e) => {
                                    setFormTitle(e.target.value);
                                    if (e.target.value) onUpdateTemplate(formTemplate?._id || '', { title: e.target.value });
                                }}
                                autoFocus
                                required
                            />
                            <TextField
                                fullWidth
                                multiline
                                variant="outlined"
                                label={capitalizeFirstLetter(t('description'))}
                                value={formDescription}
                                onChange={(e) => {
                                    setFormDescription(e.target.value);
                                    onUpdateTemplate(formTemplate?._id || '', { description: e.target.value });
                                }}
                            />
                            <Box display="flex" flexDirection={'row'} alignItems="center" gap={2}>
                                <FileUpload
                                    accept=".pdf,.jpg,.png"
                                    maxSizeMB={5}
                                    onFileSelected={(file) => {
                                        if (formTemplate?._id) {
                                            onUpdateTemplate(formTemplate._id, {}, file);
                                        }
                                    }}
                                    label={capitalizeFirstLetter(t('importSupportFile'))}
                                />
                                {formTemplate.supportDocumentFile && (
                                    <Chip
                                        label={`${formTemplate.supportDocumentFile.fileName}`}
                                        onDelete={() => {
                                            if (formTemplate?._id) {
                                                onUpdateTemplate(formTemplate._id, { removeSupportDocument: true });
                                            }
                                        }}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </CardContent>
                {!disabled && (selectedId === formTemplate?._id || (selectedId === null && sections.length === 0)) && (
                    <CardActions>
                        <Button
                            startIcon={<Icon name="addCircle" />}
                            color="info"
                            onClick={() => onAddSection(formTemplate?._id || '', 0, setSelectedId)}
                        >
                            {capitalizeFirstLetter(t('insertNewSection'))}
                        </Button>
                    </CardActions>
                )}
            </Card>

            <Box sx={{ gap: { sm: 0, md: 2 }, display: 'flex', flexDirection: 'column' }}>
                {sections.map((section, sectionIndex) => (
                    <SectionItem
                        key={section._id}
                        section={section}
                        sectionIndex={sectionIndex}
                        moveQuestion={moveQuestion}
                        selectedId={selectedId}
                        setSelectedId={setSelectedId}
                        sectionsLength={sections.length}
                        sections={sections}
                        formColor={formColor}
                        globalOffset={(questionId) => questionsNumbering[questionId] || 0}
                        onDrag={handleGlobalDrag}
                        isDragging={isDragging}
                        draggingItemId={draggingItemId}
                        onAddQuestion={onAddQuestion}
                        onAddSection={onAddSection}
                        onMoveQuestion={onMoveQuestion}
                        onUpdateQuestion={onUpdateQuestion}
                        onUpdateSection={onUpdateSection}
                        onDuplicateQuestion={onDuplicateQuestion}
                        onDeleteQuestion={onDeleteQuestion}
                        onDeleteSection={onDeleteSection}
                        onDuplicateSection={onDuplicateSection}
                        onAddQuestionOption={onAddQuestionOption}
                        onDeleteQuestionOption={onDeleteQuestionOption}
                        onUpdateQuestionOption={onUpdateQuestionOption}
                        onMoveQuestionOption={onMoveQuestionOption}
                        onMoveSection={onMoveSection}
                        disabled={disabled}
                    />
                ))}
            </Box>
        </ScrollPageContainer>
    );
};

export default FormEditor;
